"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CaliforniaRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const FEDERAL_1 = require("./FEDERAL");
/**
 * California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12.
 *
 * California sets its central clock by the MORE STRINGENT of the federal
 * status and CA's own determination, named as the `effectiveGeneratorStatus`
 * derivation below. Claims key on the resulting tier and cannot state the two
 * status axes, so "an SQG may accumulate for 180 days" is stated once rather
 * than once per way of arriving at SQG. No CA rule reads the waste-stream
 * classification, so no claim states it.
 */
const CA_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR", // TODO - we should remove this, and remove it from the derivation cases below, but only after confirming that there are safeguards against it ever being saved to the db for CA
];
/**
 * The stricter-of rule as data: whichever axis reports the more stringent
 * tier sets it. Total and non-overlapping over the sixteen status pairs, both
 * enforced. `as const` so the claim rows can structurally exclude the axes it
 * reads (see `DerivedAxes`); `satisfies` ties the table to the determinations
 * type, so every determination a claim can key on has a declared derivation.
 */
const CA_DERIVATIONS = {
    effectiveGeneratorStatus: {
        from: ["generatorStatus", "stateGeneratorStatus"],
        label: "Effective California tier",
        citation: "22 CCR 66262.13",
        description: `A facility's California generator tier is the stricter of federal
      status and California's own determination.
      LQG on either -> LQG. That is the strictest possible so it doesn't matter if the
      other is unknown. Otherwise if either is unknown, we can't be sure it's not
      LQG, so just honestly report unknown.
      The tier is never VSQG. California's regulations mention VSQGs, but define
      SQG as generating "less than 2,200 lbs" per month with no lower bound, so a
      facility meeting the federal VSQG definition is also a California SQG and
      the SQG rules apply. DTSC does not recognize VSQG in practice.`.replace(/\s+/g, " "),
        cases: [
            // LQG on either axis wins outright, even if the other is unknown.
            { when: { generatorStatus: "LQG" }, produces: "LQG" },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            // SQG or VSQG on both axes: CA recognizes no VSQG tier (see the
            // description), so these all land SQG.
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            // Insufficient information
            { when: { generatorStatus: "SQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: "VSQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: null, stateGeneratorStatus: null }, produces: null },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
/**
 * Used oil and pharmaceutical containers get California's ORDINARY chapter 12
 * treatment, so the rows below restate the chapter 12 answers tier for tier
 * rather than leaving the kind axis unstated — the same arrangement the
 * federal set uses for pharmaceuticals outside Subpart P.
 */
/**
 * Chapter 23's one-year limit, stated once for every universal waste handler —
 * California has no small/large handler split, so unlike the federal rows this
 * carries a single citation.
 */
const CA_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "22 CCR 66273.35(a)",
    jurisdiction: "CA",
};
/**
 * 66261.9 subjects these wastes to chapter 23, and 66273.1(b) supplies chapter
 * 23's standards "in lieu of regulation as hazardous wastes pursuant to
 * chapters 10 through 16" — chapter 12, where the satellite caps live, is
 * inside that range.
 */
const CA_UNIVERSAL_WASTE_CAPS = {
    kind: "NO_LIMIT",
    citation: "22 CCR 66261.9, 66273.1(b)",
    jurisdiction: "CA",
};
const CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE = "California's universal waste program is its own (22 CCR chapter 23) and its category list is " +
    "not the federal one: it adds electronic waste, cathode ray tubes and photovoltaic modules, and " +
    "it declines federal pesticides. A category chapter 23 does not cover is ordinary hazardous " +
    "waste here, on the chapter 12 limits these rows restate. Read from every line on the stream, " +
    "not any one of them — see the federal note for the production measurement behind that choice.";
const CA_ORDINARY_TREATMENT_NOTE = "California manages this kind under the ordinary generator standards of chapter 12: used oil " +
    "as a non-RCRA state hazardous waste (HSC 25250.1), so part 279's absence of a generator " +
    "storage limit never reaches a California container; pharmaceutical waste because California " +
    "never adopted 40 CFR 266 Subpart P, which leaves it fully regulated hazardous waste. " +
    "Neither kind is scoped out of chapter 12 the way universal waste is, so these rows restate " +
    "the chapter 12 answers deliberately.";
const CA_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    {
        given: { effectiveGeneratorStatus: "LQG" },
        // Narrowed to ordinary hazardous waste so the kind rows below have
        // disjoint room to claim their own contexts — see those rows for what
        // governs a California universal-waste, used-oil, or pharmaceutical
        // container instead.
        when: { areaType: "CENTRAL", wasteKind: null },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" },
        },
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: false, wasteKind: null },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" },
        },
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: true, wasteKind: null },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" },
        },
        note: "California grants the same over-200-mile extension federal law does.",
    },
    {
        given: { effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL", wasteKind: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 22 CCR 66262.15 ──────────────────────────
    // Spans every status rather than keying on the derivation — see the note.
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: null,
        },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "22 CCR 66262.15(a)",
                jurisdiction: "CA",
            },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" },
        },
        note: "The same numeric caps as the federal rule, but imposed by California's own regulation " +
            "and reaching every generator tier — California never adopted the federal VSQG " +
            "carve-out. California also adds a deadline federal law has no analog for: waste must " +
            "move within one year of the container's accumulation start date, full or not.",
    },
    // ── UNIVERSAL WASTE — 22 CCR division 4.5 chapter 23 ───────────────────
    // California runs its own universal waste program rather than adopting the
    // federal one, so these rows carry California citations end to end. Two
    // differences from the federal rows worth naming: California states the
    // one-year limit ONCE, in 66273.35(a), for every universal waste handler
    // (no small/large handler split, hence no dual citation), and the scope-out
    // is 66273.1(b) reading "an alternative set of management standards in lieu
    // of regulation as hazardous wastes pursuant to chapters 10 through 16" —
    // chapter 12 is inside that range, so 66262.15's satellite caps never
    // attach. Status-independent, so these wildcard the derivation.
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: true,
        },
        expect: {
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66273.35(a)", jurisdiction: "CA" },
        },
        note: "One year from the date the universal waste was generated or received, for every " +
            "universal waste handler — California does not split the limit by handler size the way " +
            "40 CFR 273.15(a) and 273.35(a) do.",
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: true,
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "22 CCR 66261.9, 66273.1(b)", jurisdiction: "CA" },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66273.35(a)", jurisdiction: "CA" },
        },
        note: "66261.9 makes these wastes subject to chapter 23 instead, and 66273.1(b) supplies " +
            "chapter 23's standards in lieu of chapters 10 through 16 — chapter 12 among them. So " +
            "California's own satellite caps and 3-day move window never attach, and the one-year " +
            "limit runs the same in a satellite or central area.",
    },
    // ── USED OIL AND PHARMACEUTICAL — ordinary chapter 12 ──────────────────
    // Restated tier for tier rather than left unstated: see
    // CA_ORDINARY_TREATMENT_NOTE for why neither kind escapes chapter 12 in
    // California.
    {
        given: { effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL", wasteKind: "USED_OIL" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: false, wasteKind: "USED_OIL" },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: true, wasteKind: "USED_OIL" },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL", wasteKind: "USED_OIL" },
        expect: { clock: { kind: "UNDETERMINED" } },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        when: { areaType: "SATELLITE", wasteKind: "USED_OIL" },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "22 CCR 66262.15(a)",
                jurisdiction: "CA",
            },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL", wasteKind: "PHARMACEUTICAL" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: false, wasteKind: "PHARMACEUTICAL" },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL", mustTransport200Miles: true, wasteKind: "PHARMACEUTICAL" },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL", wasteKind: "PHARMACEUTICAL" },
        expect: { clock: { kind: "UNDETERMINED" } },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    {
        when: { areaType: "SATELLITE", wasteKind: "PHARMACEUTICAL" },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "22 CCR 66262.15(a)",
                jurisdiction: "CA",
            },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" },
        },
        note: CA_ORDINARY_TREATMENT_NOTE,
    },
    // A category California's own chapter 23 program does not cover is not
    // universal waste here — pesticides are the case, the one federal-five
    // category California declined. Those containers are ordinary hazardous
    // waste on chapter 12, restated tier for tier.
    {
        given: { effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL", wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" },
        },
        note: CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: {
            areaType: "CENTRAL",
            mustTransport200Miles: false,
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" },
        },
        note: CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: {
            areaType: "CENTRAL",
            mustTransport200Miles: true,
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" },
        },
        note: CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL", wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
        expect: { clock: { kind: "UNDETERMINED" } },
        note: CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: { areaType: "SATELLITE", wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "22 CCR 66262.15(a)",
                jurisdiction: "CA",
            },
            clock: { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" },
        },
        note: CA_UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
];
/**
 * The imperative twin of the `effectiveGeneratorStatus` derivation above.
 * Sharing `CaDeterminations` keeps the two from drifting on shape; the sweep
 * holds their contents to each other.
 */
function effectiveCaliforniaStatus(ctx) {
    if (ctx.generatorStatus === "LQG" || ctx.stateGeneratorStatus === "LARGE_QUANTITY_GENERATOR") {
        return "LQG";
    }
    if (ctx.generatorStatus === null || ctx.stateGeneratorStatus === null) {
        return null;
    }
    if (ctx.generatorStatus === "SQG" ||
        ctx.generatorStatus === "VSQG" ||
        ctx.stateGeneratorStatus === "SMALL_QUANTITY_GENERATOR" ||
        ctx.stateGeneratorStatus === "VERY_SMALL_QUANTITY_GENERATOR") {
        return "SQG";
    }
    // Exhaustiveness guard: only accepts `never`, so an unhandled status
    // combination fails to compile.
    return ((..._) => null)(ctx.generatorStatus, ctx.stateGeneratorStatus);
}
/** California (DTSC) accumulation limits, 22 CCR division 4.5 chapter 12. */
class CaliforniaRules extends FEDERAL_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = CA_EXPECTATIONS;
        this.derivations = CA_DERIVATIONS;
        this.stateStatusDomain = CA_STATE_STATUS_DOMAIN;
    }
    centralClock(ctx) {
        // Universal waste is regulated under chapter 23 in lieu of chapter 12
        // (66273.1(b)), so it never reaches the generator-tier clocks below. Used
        // oil and pharmaceutical waste DO fall through deliberately: California
        // regulates used oil as a non-RCRA state hazardous waste and never
        // adopted Subpart P, so both stay on the ordinary standards.
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return CA_UNIVERSAL_WASTE_CLOCK;
        }
        const effective = effectiveCaliforniaStatus(ctx);
        if (effective === "LQG") {
            return { kind: "TIME", days: 90, citation: "22 CCR 66262.17(a)", jurisdiction: "CA" };
        }
        if (effective === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "22 CCR 66262.16(c)", jurisdiction: "CA" };
            }
            return { kind: "TIME", days: 180, citation: "22 CCR 66262.16(b)", jurisdiction: "CA" };
        }
        return { kind: "UNDETERMINED" };
    }
    satelliteCaps(ctx) {
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return CA_UNIVERSAL_WASTE_CAPS;
        }
        // Federal numbers under California's own regulation, with no VSQG
        // carve-out — CA never adopted that relief.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "22 CCR 66262.15(a)",
            jurisdiction: "CA",
        };
    }
    satelliteClock(ctx) {
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            // Same one year as a central area: chapter 23 measures from the date the
            // waste was generated or received, not from where it sits.
            return CA_UNIVERSAL_WASTE_CLOCK;
        }
        // "Full or one year, whichever comes first" — the caps cover full; this
        // is the year.
        return { kind: "TIME", days: 365, citation: "22 CCR 66262.15(a)(9)", jurisdiction: "CA" };
    }
}
exports.CaliforniaRules = CaliforniaRules;
//# sourceMappingURL=CA.js.map